import { Check, Minus, X } from "lucide-react"
import { useTranslation } from "react-i18next"

import type { AutoCheck, AutoChecks } from "@/types/submissions"

/**
 * OKS-399 — Advisory auto-checks panel inside the brand approve dialog
 * (UX Spec §"Auto-check display matrix").
 *
 * Two rows (Posted on time + Brand mention), each: icon + label + short
 * detail. **Color is never the sole signal** — every row pairs an icon
 * with text (a11y §6). Checks are decision-support ONLY; they never block
 * the approve. Values are computed server-side by `Submissions::AutoChecks`
 * and arrive on `submission.auto_checks` — the FE never re-derives.
 *
 * Locked testids: `approve-autochecks` (panel), `autocheck-posted-on-time`,
 * `autocheck-brand-mention`.
 */

interface Props {
  autoChecks: AutoChecks
}

const ROWS = [
  { key: "posted_on_time", testid: "autocheck-posted-on-time" },
  { key: "brand_mention", testid: "autocheck-brand-mention" },
] as const

export function AutoChecksPanel({ autoChecks }: Props) {
  const { t } = useTranslation()

  return (
    <div
      data-testid="approve-autochecks"
      className="bg-muted/40 flex flex-col gap-2 rounded-md border p-3"
    >
      <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
        {t("submissions.brand.autochecks.title")}
      </p>
      {ROWS.map(({ key, testid }) => (
        <AutoCheckRow
          key={key}
          testid={testid}
          label={t(`submissions.brand.autochecks.${key}.label`)}
          detail={detailText(t, key, autoChecks[key])}
          status={autoChecks[key].status}
        />
      ))}
    </div>
  )
}

interface RowProps {
  testid: string
  label: string
  detail: string
  status: AutoCheck["status"]
}

function AutoCheckRow({ testid, label, detail, status }: RowProps) {
  const Icon = status === "pass" ? Check : status === "fail" ? X : Minus
  const iconColor =
    status === "pass"
      ? "text-green-600 dark:text-green-400"
      : status === "fail"
        ? "text-red-600 dark:text-red-400"
        : "text-muted-foreground"

  return (
    <div
      data-testid={testid}
      data-status={status}
      className="flex items-center gap-2 text-sm"
    >
      <Icon aria-hidden="true" className={`size-4 shrink-0 ${iconColor}`} />
      <span className="font-medium">{label}</span>
      <span className="text-muted-foreground truncate">· {detail}</span>
    </div>
  )
}

/**
 * Resolves the per-status detail string. The `date` interpolation arrives
 * as an iso8601 string from the backend; format it to a short label
 * ("Jun 20") here so the deadline/timezone math stays server-side and the
 * copy stays locale-driven.
 */
function detailText(
  t: ReturnType<typeof useTranslation>["t"],
  checkKey: string,
  check: AutoCheck,
): string {
  const params: Record<string, string> = { ...check.detail_params }
  if (params.date) {
    params.date = new Date(params.date).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    })
  }
  return t(
    `submissions.brand.autochecks.${checkKey}.${check.detail_key}`,
    params,
  )
}
